import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Loader2, ExternalLink, AlertCircle, RefreshCcw, MessageCircle,
} from 'lucide-react';
import FFLoader from '../components/FFLoader';
import { formsApi } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('ff_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const REGIONS = [
  { value: 'us', label: 'United States (api.intercom.io)' },
  { value: 'eu', label: 'Europe (api.eu.intercom.io)' },
  { value: 'au', label: 'Australia (api.au.intercom.io)' },
];

/**
 * Per-form Intercom configuration page.
 *
 * Two inputs: Personal Access Token + region. Each submission searches
 * Intercom for an existing Contact by email; updates if found, creates
 * (role="user") if not. Custom data attributes auto-mapped from form
 * labels to Intercom contact attributes.
 */
export default function IntercomIntegrationPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const formList = await formsApi.getAll();
        const formsArray = Array.isArray(formList) ? formList : (formList?.forms || []);
        const decorated = formsArray.map((f) => {
          const i = (f?.settings?.intercom) || {};
          return {
            ...f,
            _i: {
              tokenInput: '',
              region: i.region || 'us',
              enabled: !!i.enabled,
              configured: !!i.access_token,
              maskedToken: i.access_token ? `…${i.access_token.slice(-6)}` : null,
              workspaceName: i.workspace_name || null,
              workspaceAppId: i.workspace_app_id || null,
              customAttributes: i.custom_attributes || [],
              lastError: i.last_error || null,
              saving: false,
              testing: false,
            },
          };
        });
        setForms(decorated);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load Intercom settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patchForm = (id, patch) => setForms((prev) => prev.map((f) =>
    f.id === id ? { ...f, _i: { ...f._i, ...patch } } : f
  ));

  const handleTest = async (form) => {
    const access_token = (form._i.tokenInput || '').trim();
    if (!access_token) { toast.error('Paste your Intercom access token first'); return; }
    patchForm(form.id, { testing: true });
    try {
      const { data } = await axios.post(
        `${API}/api/intercom/test`,
        { access_token, region: form._i.region },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      const count = (data.custom_attributes || []).filter(a => a.is_custom).length;
      toast.success(`Connected to ${data.workspace_name || 'Intercom workspace'} — ${count} custom attributes discovered`);
      patchForm(form.id, {
        customAttributes: data.custom_attributes || [],
        workspaceName: data.workspace_name || null,
        workspaceAppId: data.workspace_app_id || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reach Intercom');
    } finally {
      patchForm(form.id, { testing: false });
    }
  };

  const handleSave = async (form) => {
    const access_token = (form._i.tokenInput || '').trim();
    if (!access_token) { toast.error('Paste your Intercom access token first'); return; }
    patchForm(form.id, { saving: true });
    try {
      const { data } = await axios.put(
        `${API}/api/intercom/forms/${form.id}`,
        { enabled: true, access_token, region: form._i.region },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success(`Connected — new submissions will upsert Intercom Contacts`);
      patchForm(form.id, {
        enabled: true,
        configured: true,
        maskedToken: data.access_token_masked || null,
        region: data.region || form._i.region,
        workspaceName: data.workspace_name || null,
        workspaceAppId: data.workspace_app_id || null,
        customAttributes: data.custom_attributes || [],
        tokenInput: '',
        lastError: null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save Intercom connection');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleDisconnect = async (form) => {
    if (!window.confirm('Disconnect Intercom? New submissions will no longer sync.')) return;
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/intercom/forms/${form.id}`,
        { enabled: false },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      toast.success('Disconnected');
      patchForm(form.id, {
        enabled: false, configured: false, maskedToken: null,
        workspaceName: null, workspaceAppId: null, customAttributes: [],
        tokenInput: '', lastError: null,
      });
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  const handleToggle = async (form, checked) => {
    if (!form._i.configured) {
      toast.error('Save a connection first, then toggle it on.');
      return;
    }
    patchForm(form.id, { saving: true });
    try {
      await axios.put(
        `${API}/api/intercom/forms/${form.id}`,
        { enabled: checked },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      patchForm(form.id, { enabled: checked });
      toast.success(checked ? 'Intercom sync resumed' : 'Intercom sync paused');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
      patchForm(form.id, { enabled: !checked });
    } finally {
      patchForm(form.id, { saving: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <FFLoader size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="intercom-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/integrations" className="p-2 hover:bg-zinc-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#1F8DED] rounded-lg flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-zinc-900">Intercom</h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">How it works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="text-sm text-zinc-700 space-y-2 list-decimal list-inside">
              <li>
                In Intercom, go to{' '}
                <a
                  href="https://app.intercom.com/a/apps/_/developer-hub"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline inline-flex items-center gap-0.5"
                >
                  Settings → Developers → Developer Hub <ExternalLink className="w-3 h-3" />
                </a>
                . Click <strong>New app</strong>, give it a name, then open <strong>Authentication</strong> and copy the <strong>Access Token</strong>.
              </li>
              <li>Under <strong>Permissions</strong> → enable <em>Read and write contacts</em>.</li>
              <li>Pick your <strong>workspace region</strong> below. If you log into <code className="bg-zinc-100 px-1 rounded text-xs">app.intercom.com</code> → US. If <code className="bg-zinc-100 px-1 rounded text-xs">app.eu.intercom.com</code> → EU. If <code className="bg-zinc-100 px-1 rounded text-xs">app.au.intercom.com</code> → AU.</li>
              <li>Paste the access token below, click <strong>Test</strong>, then <strong>Save</strong>.</li>
              <li>Each new submission searches Intercom by email — if a Contact already exists, we update it; otherwise we create a new <code className="bg-zinc-100 px-1 rounded text-xs">role: user</code> contact.</li>
            </ol>
            <p className="text-xs text-zinc-500">
              Form labels matching Intercom <strong>custom data attributes</strong> (case-insensitive) are sent automatically. Name and phone are auto-extracted from Full Name and Phone questions.
            </p>
          </CardContent>
        </Card>

        <section className="space-y-3" data-testid="forms-section">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-zinc-900">Your forms</h2>
            <span className="text-xs text-zinc-500">{forms.length} {forms.length === 1 ? 'form' : 'forms'}</span>
          </div>

          {forms.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-zinc-500">
              No forms yet. <Link to="/dashboard" className="text-zinc-900 underline">Go to dashboard</Link>.
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {forms.map((form) => (
                <Card key={form.id} data-testid={`form-row-${form.id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-900 truncate">{form.title || 'Untitled form'}</p>
                        {form._i.configured ? (
                          <p className="text-xs text-zinc-500 mt-0.5">
                            Connected{form._i.workspaceName ? <> to <strong>{form._i.workspaceName}</strong></> : null}
                            {' '}· region <code className="px-1 bg-zinc-100 rounded">{form._i.region}</code>
                            {' '}· token <code className="px-1 bg-zinc-100 rounded">{form._i.maskedToken}</code>
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-400 mt-0.5">Not connected</p>
                        )}
                        {form._i.lastError && (
                          <p className="text-xs text-red-600 mt-1 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Last sync failed: {form._i.lastError}
                          </p>
                        )}
                      </div>
                      {form._i.configured && (
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-zinc-500">{form._i.enabled ? 'Active' : 'Paused'}</span>
                          <Switch
                            checked={form._i.enabled}
                            onCheckedChange={(c) => handleToggle(form, c)}
                            data-testid={`intercom-toggle-${form.id}`}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-zinc-500">
                          Access token{form._i.configured ? ' (paste new to replace)' : ''}
                        </Label>
                        <Input
                          placeholder="dG9rOg..."
                          value={form._i.tokenInput}
                          onChange={(e) => patchForm(form.id, { tokenInput: e.target.value })}
                          data-testid={`intercom-token-${form.id}`}
                          className="font-mono text-xs"
                          type="password"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Workspace region</Label>
                        <Select
                          value={form._i.region}
                          onValueChange={(v) => patchForm(form.id, { region: v })}
                        >
                          <SelectTrigger className="text-xs" data-testid={`intercom-region-${form.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {REGIONS.map((r) => (
                              <SelectItem key={r.value} value={r.value} className="text-xs">
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTest(form)}
                        disabled={form._i.testing || form._i.saving}
                        data-testid={`intercom-test-${form.id}`}
                      >
                        {form._i.testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
                        Test
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSave(form)}
                        disabled={form._i.saving || form._i.testing}
                        data-testid={`intercom-save-${form.id}`}
                      >
                        {form._i.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        {form._i.configured ? 'Replace' : 'Save'}
                      </Button>
                      {form._i.configured && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect(form)}
                          disabled={form._i.saving}
                          data-testid={`intercom-disconnect-${form.id}`}
                        >
                          Disconnect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
